import { memo } from 'react'
import { Node } from 'reactflow'
import { Button } from "@/shared/components/ui/button"
import { Input } from "@/shared/components/ui/input"
import { Label } from "@/shared/components/ui/label"
import { Textarea } from "@/shared/components/ui/textarea"
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select"
import { Slider } from "@/shared/components/ui/slider"
import { Plus, Trash2, GitBranch, PenLine, Brain } from "lucide-react"
import type { Condition } from '../../nodes/types'

interface ModelOption {
  id: string
  name: string
  description: string
  providerId: string
  providerName: string
  configured?: boolean
}

interface ProviderGroup {
  provider: {
    id: string
    name: string
    configured: boolean
  }
  models: ModelOption[]
}

interface RouterNodeConfigProps {
  selectedNode: Node
  nodes: Node[]
  modelsByProvider: ProviderGroup[]
  defaultModel: string
  onUpdateNodeData: (field: string, value: unknown) => void
  onOpenPromptEditor: (field: string, content: string, title: string, description: string) => void
}

export const RouterNodeConfig = memo(({ 
  selectedNode, 
  nodes,
  modelsByProvider,
  defaultModel,
  onUpdateNodeData,
  onOpenPromptEditor
}: RouterNodeConfigProps) => {
  const conditions: Condition[] = selectedNode.data.conditions || []
  
  // Temperature 默认值
  const currentTemperature = selectedNode.data.temperature ?? 0.7
  
  // 添加新条件
  const handleAddCondition = () => {
    const newCondition: Condition = {
      id: `condition-${Date.now()}`,
      name: `条件 ${conditions.length + 1}`,
      condition: '',
      target: ''
    }
    
    onUpdateNodeData('conditions', [...conditions, newCondition])
  }
  
  // 删除条件
  const handleDeleteCondition = (index: number) => {
    const newConditions = conditions.filter((_, i) => i !== index)
    onUpdateNodeData('conditions', newConditions)
  }
  
  // 更新条件
  const handleUpdateCondition = (index: number, field: keyof Condition, value: string) => {
    const newConditions = [...conditions]
    newConditions[index] = { ...newConditions[index], [field]: value }
    onUpdateNodeData('conditions', newConditions)
  }

  return (
    <>
      <div className="space-y-2">
        <Label>路由节点名称</Label>
        <Input
          value={selectedNode.data.label || ''}
          onChange={(e) => onUpdateNodeData('label', e.target.value)}
          placeholder="路由节点名称"
        />
      </div>

      <div className="space-y-2">
        <Label>描述</Label>
        <Textarea
          value={selectedNode.data.description || ''}
          onChange={(e) => onUpdateNodeData('description', e.target.value)}
          placeholder="路由节点的描述信息"
          rows={2}
        />
      </div>

      {/* 模型配置 */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <Brain className="h-4 w-4" />
          路由模型
        </Label>
        <Select
          value={selectedNode.data.model || defaultModel}
          onValueChange={(value) => onUpdateNodeData('model', value)}
        >
          <SelectTrigger>
            <SelectValue placeholder="选择模型" />
          </SelectTrigger>
          <SelectContent>
            {modelsByProvider.map((group) => (
              <div key={group.provider.id}>
                <div className="px-2 py-1.5 text-xs font-medium text-slate-500 bg-slate-50">
                  {group.provider.name}
                  {!group.provider.configured && <span className="text-red-500 ml-1">*</span>}
                </div>
                {group.models.map((model) => (
                  <SelectItem
                    key={model.id}
                    value={model.id}
                    disabled={!group.provider.configured}
                  >
                    <div className="flex flex-col">
                      <span>{model.name}</span>
                      {model.description && (
                        <span className="text-xs text-slate-500">{model.description}</span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </div>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Temperature 配置 */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Temperature</Label>
          <span className="text-xs text-slate-500">{Number(currentTemperature).toFixed(2)}</span>
        </div>
        <Slider
          value={Number(currentTemperature)}
          onValueChange={(value) => onUpdateNodeData('temperature', Math.round(value * 100) / 100)}
          max={2}
          min={0}
          step={0.1}
          className="w-full"
        />
        <p className="text-xs text-slate-500">
          控制路由决策的随机性。较低的值使决策更确定，较高的值增加随机性。
        </p>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label className="flex items-center gap-2">
            <GitBranch className="h-4 w-4" />
            路由条件配置
          </Label>
          <Button 
            onClick={handleAddCondition}
            size="sm"
            className="flex items-center gap-1"
          >
            <Plus className="h-3 w-3" />
            添加条件
          </Button>
        </div>
        
        {conditions.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex items-center justify-center py-8">
              <div className="text-center text-slate-500">
                <GitBranch className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">还没有配置路由条件</p>
                <p className="text-xs mt-1">点击"添加条件"开始配置</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {conditions.map((condition, index) => (
              <Card key={condition.id} className="border-amber-200 bg-amber-50/50">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-amber-400" />
                      条件 {index + 1}
                    </CardTitle>
                    <Button
                      onClick={() => handleDeleteCondition(index)}
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <Label className="text-xs">条件名称</Label>
                    <Input
                      value={condition.name}
                      onChange={(e) => handleUpdateCondition(index, 'name', e.target.value)}
                      placeholder="例如：满足条件A"
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label className="text-xs">判断逻辑</Label>
                      <Button
                        onClick={() => onOpenPromptEditor(
                          `conditions.${index}.condition`,
                          condition.condition,
                          `条件 ${index + 1} - 判断逻辑`,
                          "详细描述这个路由条件的判断逻辑"
                        )}
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2 text-xs"
                      >
                        <PenLine className="h-3 w-3 mr-1" />
                        编辑
                      </Button>
                    </div>
                    <Textarea
                      value={condition.condition}
                      onChange={(e) => handleUpdateCondition(index, 'condition', e.target.value)}
                      placeholder="详细描述判断条件的逻辑..."
                      rows={3}
                      className="text-xs"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-xs">目标节点</Label>
                    <Select
                      value={condition.target}
                      onValueChange={(value) => handleUpdateCondition(index, 'target', value)}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="选择目标节点" />
                      </SelectTrigger>
                      <SelectContent>
                        {nodes
                          .filter(node => node.id !== selectedNode.id) // 排除自己
                          .map((node) => (
                            <SelectItem key={node.id} value={node.id}>
                              <div className="flex flex-col">
                                <span>{node.data.label || `未命名${node.type}`}</span>
                                <span className="text-xs text-slate-500">
                                  {node.type}
                                </span>
                              </div>
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1">
                      当满足此条件时，流程将跳转到指定的节点
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <div className="pt-4 border-t border-slate-200">
        <div className="text-xs text-slate-500">
          <p className="mb-1">💡 使用提示：</p>
          <ul className="space-y-1 text-xs">
            <li>• 每个条件对应一个输出分支</li>
            <li>• 条件按顺序评估，第一个匹配的条件将被执行</li>
            <li>• 建议添加一个默认条件作为兜底</li>
          </ul>
        </div>
      </div>
    </>
  )
})

RouterNodeConfig.displayName = 'RouterNodeConfig'